import { PAP_Sign_SignatureRequest } from '@dropbox/pap-events/sign/sign_signature_request';
import { ResponseError } from '@dropbox/sign-internal-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ErrorWithContext } from 'common/utils/sentry/error-with-context';
import delay from 'hellospa/common/utils/delay';
import invariant from 'invariant';
import { useAtomValue } from 'jotai';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import React from 'react';
import { useHistory } from 'react-router';
import { useBundleProps } from 'signer-app/bundle-props';
import { useSignerAppClient } from 'signer-app/context/signer-app-client';
import { extractSavedData } from 'signer-app/signer-experience/saved-data-utils';
import { SignatureRequestResponse } from 'signer-app/signer-experience/types';
import { accessibleViewAtom } from 'signer-app/signer-signature-document/accessible-document-toggle';
import { Field } from 'signer-app/types/editor-types';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import { logPAPEvent } from 'signer-app/utils/log-pap-event';
import { redirectTo } from 'signer-app/utils/redirect';
import { getEidRedirect } from './get-eid-redirect';
import { getPostsignUrl } from './get-postsign-url';
import { responseToSentryContext } from './response-to-sentry-context';
import { signerExperienceQueryKey } from './types';

type UseFinalizeParams = {
  signatureRequestId: string;
  accessCode: string | null;
  tsmGuid: string;
  isSavingRef: React.MutableRefObject<boolean>;
  draftRef: React.MutableRefObject<Record<Field['id'], Field> | null>;
};

export function useFinalize({
  signatureRequestId,
  accessCode,
  tsmGuid,
  isSavingRef,
  draftRef,
}: UseFinalizeParams) {
  const client = useSignerAppClient();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { signerAccountId, signerDbxAccountId } = useBundleProps();
  const isFormViewToggleActive = useAtomValue(accessibleViewAtom);
  const preserveOriginalRequired = useFeatureFlag(
    'sign_core_2026_02_19_fix_conditional_logic_original_required',
  );

  const [redirectUrl, setRedirectUrl] = React.useState<string | undefined>();

  const queryKey = signerExperienceQueryKey(signatureRequestId, accessCode);

  const { mutate: finalize, isLoading: isFinalizing } = useMutation({
    useErrorBoundary: true,
    mutationKey: [...queryKey, 'finalize'],
    mutationFn: async () => {
      while (draftRef.current != null || isSavingRef.current) {
        // eslint-disable-next-line no-await-in-loop
        await delay(500);
      }

      const queryData =
        queryClient.getQueryData<SignatureRequestResponse>(queryKey);

      invariant(queryData, 'Cannot finalize without query data');

      invariant(!isSavingRef.current, 'Cannot finalize while saving fields');

      const data = Object.fromEntries(
        queryData.signatureRequest.fields.map((field) => [
          field.id,
          extractSavedData(field, { preserveOriginalRequired }),
        ]),
      );

      try {
        return await client.signerApi.signerAppFinalizeAction({
          guid: tsmGuid,
          accessCode: accessCode ?? undefined,
          data,
          isSmsDelivery: queryData.isComingFromSmsDelivery ?? false,
        });
      } catch (error) {
        if ((error as Error).name === 'ResponseError') {
          const response = (error as ResponseError).response;
          const status = response.status;

          if (status === 410) {
            await redirectTo('/info/externalNothingToSign');
            return;
          } else if (status === 401) {
            await redirectTo(
              `/account/logIn?on_login_redirect_url=${encodeURIComponent(location.href)}`,
            );
            return;
          } else {
            throw new ErrorWithContext(
              `Unexpected ResponseError HTTP ${status}`,
              {
                tags: {
                  errorType: 'signerAppFinalizeFailure',
                  httpStatus: status,
                },
                contexts: {
                  response: await responseToSentryContext(response),
                  request: {
                    signatureRequestId,
                    tsmGuid,
                    hasAccessCode: !!accessCode,
                  },
                },
              },
            );
          }
        }
        throw error;
      }
    },
    onSuccess: (response) => {
      if (!response) {
        return;
      }

      const queryData =
        queryClient.getQueryData<SignatureRequestResponse>(queryKey);

      logPAPEvent(
        PAP_Sign_SignatureRequest({
          signatureRequestId,
          enableFormView: queryData?.isMobileReady,
          formViewToggle: queryData?.isMobileReady
            ? isFormViewToggleActive
            : undefined,
          signatureFrame:
            queryData?.signatureRequest?.signerOptions?.signatureFrame ?? false,
        }),
        signerDbxAccountId,
        signerAccountId,
      );

      const eidRedirect = getEidRedirect(signatureRequestId, response);

      if (eidRedirect) {
        history.push(eidRedirect);
      } else if (response.postsignPageType && response.cacheKey) {
        redirectTo(
          getPostsignUrl({
            type: response.postsignPageType,
            isEmbedded: queryData?.isEmbedded ?? false,
            signerCacheKey: response.cacheKey,
          }),
        );
      } else if (
        response.writeBack &&
        response.redirectUrl &&
        response.isTrusted &&
        (HfReactHelper.matchesHsDomain(response.redirectUrl, '/') ||
          HfReactHelper.matchesHsDomain(response.redirectUrl, '/home/index'))
      ) {
        redirectTo(`${response.redirectUrl}?writeBackSuccess=true`);
      } else {
        if (response.redirectUrl) {
          setRedirectUrl(response.redirectUrl);
        }
        history.push(`/sign/${signatureRequestId}/complete/`);
      }
    },
  });

  return {
    finalize,
    isFinalizing,
    redirectUrl,
  };
}
